import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于向日葵视频',
  description: '向日葵视频是一个免费高清在线视频观看平台，提供榴莲视频、红杏视频等热播内容。',
  url: 'https://www.xiangrikui-video.com/about',
};

const orgSchemaFull = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '向日葵视频',
  url: 'https://www.xiangrikui-video.com/',
  description: '向日葵视频提供免费高清在线观看，榴莲视频、红杏视频等热播电影电视剧综艺全集，流畅不缓冲，在线播放体验佳。',
  foundingDate: '2023',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'support@xiangrikui-video.com',
    availableLanguage: ['Chinese'],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '向日葵视频需要注册吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '不需要！向日葵视频所有内容完全免费，无需注册即可直接观看，没有任何隐藏收费。',
      },
    },
    {
      '@type': 'Question',
      name: '如何联系向日葵视频团队？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过邮箱 support@xiangrikui-video.com 联系我们，或在关于我们页面提交留言。我们会在24小时内回复。',
      },
    },
    {
      '@type': 'Question',
      name: '向日葵视频的内容多久更新一次？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '向日葵视频每天都会更新最新内容，包括热播电影、电视剧、综艺节目等，确保您能第一时间看到最新最热的视频内容。',
      },
    },
  ],
};

export default function About() {
  return (
    <SEO
      title="关于我们"
      description="了解向日葵视频的品牌故事、用户口碑和联系方式。向日葵视频提供免费高清在线观看，榴莲视频、红杏视频等热播内容。"
      keywords="向日葵视频,关于我们,品牌介绍,联系我们,视频平台"
      canonicalUrl="https://www.xiangrikui-video.com/about"
      schemaData={{ ...aboutSchema, ...orgSchemaFull }}
    >
      <ScrollToTop />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1">
          <div className="bg-background-100">
            <div className="container-main">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <div className="pb-6">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  关于我们
                </h1>
                <p className="text-sm text-foreground-500 mt-2">了解向日葵视频的故事</p>
              </div>
            </div>
          </div>

          {/* Brand Introduction */}
          <section id="intro" className="py-12 md:py-16">
            <div className="container-main max-w-[900px]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    品牌介绍
                  </h2>
                  <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      向日葵视频成立于2023年，是一个专注于提供<strong>免费高清在线观看</strong>服务的视频平台。我们致力于为用户打造极致的观影体验，让每个人都能随时随地享受高质量的影视内容。
                    </p>
                    <p>
                      平台汇集了<strong>榴莲视频</strong>、<strong>红杏视频</strong>等热门内容品牌，涵盖电影、电视剧、综艺、动漫、纪录片等多种类型。无论你喜欢什么类型的内容，在向日葵视频都能找到。
                    </p>
                    <p>
                      我们的核心理念是"流畅不缓冲"。通过先进的视频编码技术和全球CDN加速网络，我们确保每一位用户都能获得流畅稳定的播放体验。高清画质、极速加载，这就是向日葵视频的承诺。
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20modern%20creative%20workspace%20with%20warm%20lighting%2C%20people%20collaborating%20around%20a%20large%20screen%20showing%20video%20content%2C%20cozy%20atmosphere%2C%20minimalist%20design%2C%20warm%20amber%20and%20wood%20tones&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="向日葵视频品牌介绍"
                    title="向日葵视频 - 品牌介绍"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Reputation / Stats */}
          <section id="reputation" className="py-12 md:py-16 bg-background-100">
            <div className="container-main">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500 mt-2">来自用户的真实评价与数据</p>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                {[
                  { value: '98%', label: '用户满意度' },
                  { value: '1000万+', label: '累计用户' },
                  { value: '5000+', label: '影片资源' },
                  { value: '4.9/5', label: '用户评分' },
                ].map((item) => (
                  <div key={item.label} className="text-center p-6 bg-background-50 rounded-xl border border-background-200/60">
                    <p className="text-2xl md:text-3xl font-bold text-primary-500">{item.value}</p>
                    <p className="text-sm text-foreground-500 mt-1">{item.label}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-[900px] mx-auto">
                {[
                  {
                    name: '张先生',
                    role: '电影爱好者',
                    comment: '向日葵视频真的太棒了！画质清晰，播放流畅，关键是完全免费。榴莲视频的内容特别有深度，我已经推荐给所有朋友了。',
                    rating: 5,
                  },
                  {
                    name: '李女士',
                    role: '追剧达人',
                    comment: '红杏视频的剧集更新很快，画质也超级好。用向日葵视频追剧已经成了我每天的必备节目，不缓冲的体验太舒服了！',
                    rating: 5,
                  },
                  {
                    name: '王同学',
                    role: '大学生',
                    comment: '作为一个学生党，免费高清的观影平台真的太友好了。综艺节目更新及时，纪录片资源也很丰富，学到了很多知识。',
                    rating: 5,
                  },
                ].map((review) => (
                  <div key={review.name} className="bg-background-50 border border-background-200/60 rounded-xl p-5">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-secondary-500 text-sm" />
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.comment}</p>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="py-12 md:py-16">
            <div className="container-main max-w-[900px]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-500 mb-6 leading-relaxed">
                    有任何问题或建议？欢迎通过以下方式联系我们，或填写表单给我们留言。我们会在24小时内回复。
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                        <i className="ri-mail-line text-primary-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">邮箱</p>
                        <p className="text-sm text-foreground-500">support@xiangrikui-video.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center shrink-0">
                        <i className="ri-phone-line text-secondary-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">电话</p>
                        <p className="text-sm text-foreground-500">400-888-8888</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                        <i className="ri-map-pin-line text-accent-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">地址</p>
                        <p className="text-sm text-foreground-500">北京市朝阳区科技园区创新路88号</p>
                      </div>
                    </div>
                  </div>
                </div>

                <ContactForm />
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="py-12 md:py-16 bg-background-100">
            <div className="container-main max-w-[800px]">
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
              />
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  常见问题
                </h2>
              </div>
              <div className="space-y-3">
                {[
                  { q: '向日葵视频需要注册吗？', a: '不需要！向日葵视频所有内容完全免费，无需注册即可直接观看，没有任何隐藏收费。打开网站就能看，就是这么简单。' },
                  { q: '如何联系向日葵视频团队？', a: '您可以通过邮箱 support@xiangrikui-video.com 联系我们，或在关于我们页面提交留言。我们的客服团队会在24小时内回复您的咨询。' },
                  { q: '向日葵视频的内容多久更新一次？', a: '向日葵视频每天都会更新最新内容，包括热播电影、电视剧、综艺节目等。我们与榴莲视频、红杏视频等内容方保持紧密合作，确保您能第一时间看到最新最热的视频内容。' },
                ].map((faq, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
                    <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-background-100 transition-colors duration-200">
                      <h4 className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</h4>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 text-lg shrink-0" />
                    </summary>
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </SEO>
  );
}

function ContactForm() {
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') return;

    const formData = new FormData(form);
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9n4idm8mbnljin59tlg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      if (response.ok) {
        form.reset();
      }
    } catch (_) {
      // silently handle
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-background-100 rounded-xl p-6 border border-background-200/60" data-readdy-form="">
      <h3 className="text-base font-semibold text-foreground-800 mb-4">给我们留言</h3>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-hp"
      />

      <div className="space-y-3">
        <div>
          <input
            type="text"
            name="name"
            placeholder="您的姓名"
            className="w-full h-10 px-3 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
            required
          />
        </div>
        <div>
          <input
            type="email"
            name="email"
            placeholder="您的邮箱"
            className="w-full h-10 px-3 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
            required
          />
        </div>
        <div>
          <textarea
            name="message"
            placeholder="您的留言（限500字）"
            maxLength={500}
            rows={4}
            className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
            required
          />
        </div>
        <button
          type="submit"
          className="whitespace-nowrap w-full h-10 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all duration-200 text-sm"
        >
          发送留言
        </button>
      </div>
    </form>
  );
}